import { AUTH_URL } from 'src/configs';
import request from 'src/request';

import { ListMeta } from './adminUsers';

export type ActorType = 'admin' | 'customer' | 'system' | 'anonymous';
export type LogSource = 'cloud' | 'local';

export type ActivityLog = {
  id: string;
  actor_type: ActorType | string;
  actor?: string | null;
  actor_email?: string | null;
  action: string;
  method?: string;
  path?: string;
  status: number;
  duration_ms?: number;
  ip?: string | null;
  user_agent?: string | null;
  meta?: Record<string, unknown> | null;
  source?: LogSource | string | null;
  host?: string | null;
  created_at: string;
};

export type ThirdPartyLog = {
  id: string;
  provider: string;
  method?: string | null;
  url?: string | null;
  status_code?: number | null;
  ok: boolean;
  duration_ms?: number;
  error?: string | null;
  response_excerpt?: string | null;
  /** What we sent (body + query, secrets redacted). Failures only. */
  request_excerpt?: string | null;
  context?: string | null;
  source?: LogSource | string | null;
  host?: string | null;
  created_at: string;
};

export type ThirdPartySummary = {
  avg_duration_ms: number;
  max_duration_ms: number;
  failures: number;
};

export type ProviderHealth = {
  provider: string;
  calls: number;
  failures: number;
  avg_duration_ms: number;
  max_duration_ms: number;
  last_call_at?: string | null;
};

const buildQuery = (params: Record<string, string | number | boolean | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const query = qs.toString();
  return query ? `?${query}` : '';
};

export const formatActorTypeLabel = (type?: string) => {
  if (!type) return '—';
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

export const formatDurationMs = (ms?: number | null) => {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(ms >= 10000 ? 0 : 1)}s`;
};

export const formatProviderLabel = (provider?: string) => {
  if (!provider) return '—';
  return provider
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

export const formatSourceLabel = (source?: string | null) => {
  if (!source) return '—';
  return source.charAt(0).toUpperCase() + source.slice(1).toLowerCase();
};

export const statusTone = (status?: number | null): 'ok' | 'client' | 'server' | 'neutral' => {
  if (status == null || !Number.isFinite(status)) return 'neutral';
  if (status >= 200 && status < 400) return 'ok';
  if (status >= 400 && status < 500) return 'client';
  if (status >= 500) return 'server';
  return 'neutral';
};

export const durationTone = (ms?: number | null): 'fast' | 'slow' | 'critical' | 'neutral' => {
  if (ms == null || !Number.isFinite(ms)) return 'neutral';
  if (ms < 1000) return 'fast';
  if (ms <= 5000) return 'slow';
  return 'critical';
};

export const dateInputToIso = (value: string, endOfDay = false) => {
  if (!value) return undefined;
  const date = new Date(endOfDay ? `${value}T23:59:59.999` : `${value}T00:00:00.000`);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

export const fetchActivityLogs = async (params: {
  page?: number;
  limit?: number;
  actor_type?: ActorType | '';
  actor?: string;
  actor_search?: string;
  action?: string;
  status?: string | number;
  source?: LogSource | '';
  from?: string;
  to?: string;
}) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/logs/activity${buildQuery(params)}`,
  );
  return {
    activity_logs: (response.data.activity_logs || []) as ActivityLog[],
    meta: response.data.meta as ListMeta,
  };
};

export const fetchUserActivityLogs = async (
  userId: string,
  params: {
    page?: number;
    limit?: number;
    action?: string;
    status?: string | number;
    source?: LogSource | '';
    from?: string;
    to?: string;
  } = {},
) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/users/${userId}/activity${buildQuery(params)}`,
  );
  return {
    activity_logs: (response.data.activity_logs || []) as ActivityLog[],
    meta: response.data.meta as ListMeta,
  };
};

export const fetchThirdPartyLogs = async (params: {
  page?: number;
  limit?: number;
  provider?: string;
  ok?: boolean;
  status_code?: string | number;
  min_duration_ms?: string | number;
  source?: LogSource | '';
  from?: string;
  to?: string;
}) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/logs/third-party${buildQuery(params)}`,
  );
  return {
    third_party_logs: (response.data.third_party_logs || []) as ThirdPartyLog[],
    summary: (response.data.summary || {
      avg_duration_ms: 0,
      max_duration_ms: 0,
      failures: 0,
    }) as ThirdPartySummary,
    meta: response.data.meta as ListMeta,
  };
};

export const fetchLogProviders = async () => {
  const response = await request.get(`${AUTH_URL}/v1/admin/logs/providers`);
  return {
    providers: (response.data.providers || []) as ProviderHealth[],
  };
};
